import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Modal from '../../Modal';
import Carousel from '../../Carousel';
import './InfoSwiper.scss';
import Responsive from '../../../modules/Responsive';

/**
 * Swiper wrapper to receive swipers
 *
 * @example
 * export default class MyNewSwiper extends React.Component {
 *   static propTypes = {
 *   renderContainer: PropTypes.object,
 *   };
 *
 *   static defaultProps = {
 *     renderContainer: null,
 *   };
 *
 *   render() {
 *     const { renderContainer } = this.props;
 *     return (
 *       <InfoSwiper
 *         renderContainer={renderContainer}
 *         className="name-of-swiper"
 *         onSlideViewed={page_num => analytics.track("event_name_for_tracking", { page_num })}
 *         onDismissed={() => analytics.track("event_name_for_tracking")}
 *       >
 *         <section className="without-background">
 *           <div className="slide-1">
 *             <h1>Slide 1 header</h1>
 *             <p>Slide 1 content</p>
 *           </div>
 *         </section>
 *         <section className="with-background">
 *           <div className="slide-content">
 *             <h1>Slide 2 header</h1>
 *             <p><span className="image-or-icon" />Slide 2 content</p>
 *             <p><span className="image-or-icon" />Slide 2 more content</p>
 *           </div>
 *         </section>
 *       </InfoSwiper>
 *     );
 *   }
 * }
 */
export default class InfoSwiper extends React.Component {
  static propTypes = {
    /** INTERNAL USE -- DO NOT SET (set by modalManager)
     The DOM node the modal was rendered into. */
    renderContainer: PropTypes.object,
    /** The classname for the swiper */
    className: PropTypes.string,
    /** The swiper pages as react elements */
    children: PropTypes.array.isRequired,
    /** Callback invoked when a slide is viewed, including back navigation and the first slide
     * being presented. Slide numbers are 0-indexed */
    onSlideViewed: PropTypes.func,
    /** Callback invoked when the swiper is dismissed. Slide numbers are 0-indexed. */
    onDismissed: PropTypes.func,
    /** Classes to apply to the modal and carousel rendered by this component */
    classes: PropTypes.shape({
      modal: PropTypes.string,
      carousel: PropTypes.string,
    }),
    modalProps: PropTypes.object,
    /** Slide number (zero indexed) to init swiper on */
    initialSlideIndex: PropTypes.number,
    /** Used by modalManager */
    initialOpen: PropTypes.bool,
    onCloseModal: PropTypes.func,
    /** Enable/disable desktop controls, left unset it will auto-detect if they are required */
    enableControls: PropTypes.bool,
  };

  static defaultProps = {
    renderContainer: null,
    className: '',
    classes: {},
    onCloseModal: null,
    initialOpen: true,
    initialSlideIndex: 0,
    onSlideViewed: () => null,
    onDismissed: () => null,
    enableControls: null,
    modalProps: {},
  };

  state = {
    currentSlideIndex: 0,
  };

  modal = null;

  componentDidMount() {
    const { initialSlideIndex } = this.props;
    this.setState({ currentSlideIndex: initialSlideIndex });
    this.props.onSlideViewed(initialSlideIndex);
  }

  componentDidUpdate(_, prevState) {
    if (this.state.currentSlideIndex !== prevState.currentSlideIndex) {
      this.props.onSlideViewed(this.state.currentSlideIndex);
    }
  }

  /**
   * Updates the modal reference
   */
  setModal = ref => {
    this.modal = ref;
  };

  /** Closes the modal */
  closeModal = () => {
    if (this.modal) {
      return this.modal.close();
    }
    return Promise.resolve();
  };

  /**
   * Aliasing `closeModal` to `close` so InfoSwper can be used with
   * `withModal`, which expects `close`.
   */
  close = this.closeModal;

  /**
   * Handles the `onClose` event of the `Modal`
   */
  handleCloseModal = () => {
    const { onCloseModal } = this.props;
    if (typeof onCloseModal === 'function') {
      onCloseModal();
    }
    this.props.onDismissed(this.state.currentSlideIndex);
  };

  /**
   * Fires off whenever user swipes through slides
   */
  onSwipe = (_, slideIndex) => {
    this.setState({ currentSlideIndex: slideIndex });
  };

  render() {
    const {
      children,
      renderContainer,
      className,
      classes,
      initialOpen,
      initialSlideIndex,
      enableControls,
      modalProps,
    } = this.props;

    const currentSlideClass = `info-swiper-slide-${this.state
      .currentSlideIndex + 1}`;
    const modalClasses = classNames(
      `${className}-component`,
      'info-swiper-modal-component',
      classes.modal,
      currentSlideClass,
    );
    const carouselClasses = classNames(
      `${className}-carousel`,
      'info-swiper-carousel-component',
      classes.carousel,
    );

    return (
      <Modal
        ref={this.setModal}
        initialOpen={initialOpen}
        onClose={this.handleCloseModal}
        renderContainer={renderContainer}
        className={modalClasses}
        {...modalProps}
      >
        <Carousel
          className={carouselClasses}
          autoPlayInterval={null}
          onChangeSlide={this.onSwipe}
          enableControls={
            enableControls === null
              ? Responsive.checkBreakpoints().layout.desktop
              : enableControls
          }
          initialSlideIndex={initialSlideIndex}
        >
          {children}
        </Carousel>
      </Modal>
    );
  }
}
